<template>
  <div class="proxy-selector">
    <el-input
      :value="selectedProxyName"
      :placeholder="disabled ? '请先选择商户' : '请选择代理'"
      :readonly="true"
      :disabled="disabled"
      @mouseenter.native="hovering = true"
      @mouseleave.native="hovering = false"
    >
      <template slot="suffix">
        <i
          v-show="selectedProxyName && hovering"
          class="el-input__icon el-icon-circle-close"
          @click="handleClear"
          style="cursor: pointer;"
        ></i>
      </template>
      <template slot="append">
        <el-button
          icon="el-icon-more-outline"
          @click="openDialog"
          class="select-button"
        >
        </el-button>
      </template>
    </el-input>

    <el-dialog
      title="选择代理"
      :visible.sync="dialogVisible"
      width="70%"
      :before-close="handleDialogClose"
      :modal-append-to-body="true"
      :append-to-body="true"
      custom-class="proxy-dialog"
    >
      <div class="search-area">
        <el-input
          v-model="keyword"
          placeholder="请输入代理名称搜索"
          clearable
          style="width: 300px;"
          @clear="handleSearchClear"
          @keyup.enter.native="handleSearch"
        ></el-input>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜 索</el-button>
      </div>

      <el-table
        :data="proxyList"
        :height="tableHeight"
        border
        style="width: 100%; margin-top: 10px;"
        highlight-current-row
        @row-click="handleRowClick"
        @row-dblclick="handleRowDblClick"
        ref="proxyTable"
      >
        <el-table-column prop="id" label="ID" width="200"></el-table-column>
        <el-table-column
          prop="name"
          label="代理名称"
          sortable
          :sort-method="sortByName"
        ></el-table-column>
        <el-table-column prop="merchantName" label="所属商户" width="200"></el-table-column>
        <el-table-column prop="ip" label="IP地址" width="150"></el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="200" sortable></el-table-column>
      </el-table>

      <el-pagination
        style="margin-top: 10px; float: right;"
        background
        layout="prev, pager, next"
        :current-page.sync="page"
        :page-size.sync="rows"
        :total="tableTotal"
        @current-change="currentPageChanged"
      >
      </el-pagination>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleConfirm" :disabled="!selectedProxy">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import httpClient from '../httpClient.js'

export default {
  name: "ProxySelector",
  props: {
    value: {
      type: [Number, String],
      default: null
    },
    merchantId: {
      type: [Number, String],
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      keyword: '',
      page: 1,
      rows: 20,
      tableTotal: 0,
      proxyList: [],
      tableHeight: 400,
      selectedProxy: null,
      selectedProxyName: '',
      hovering: false
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadProxyName(newVal)
        } else {
          this.selectedProxyName = ''
          this.selectedProxy = null
        }
      }
    },
    merchantId(newVal, oldVal) {
      // 商户变化时清除当前选择（因为已选择的代理可能不属于新商户）
      if (newVal !== oldVal && this.value) {
        this.handleClear()
      }
      // 重置分页
      this.page = 1
      // 如果对话框打开，立即刷新列表
      if (this.dialogVisible) {
        this.queryList()
      }
    }
  },
  methods: {
    queryList() {
      let data = {
        page: this.page,
        rows: this.rows,
        params: {
          keyword: this.keyword
        }
      }
      if (this.merchantId) {
        data.params.merchantId = this.merchantId
      }
      httpClient.post("/api/proxy/query", data).then((response) => {
        this.tableTotal = response.data.data.total
        this.proxyList = response.data.data.list
      })
    },
    currentPageChanged() {
      this.queryList()
    },
    handleSearch() {
      this.page = 1
      this.queryList()
    },
    handleSearchClear() {
      this.keyword = ''
      this.page = 1
      this.queryList()
    },
    handleRowClick(row) {
      this.$refs.proxyTable.setCurrentRow(row)
      this.selectedProxy = row
    },
    handleRowDblClick(row) {
      this.selectedProxy = row
      this.handleConfirm()
    },
    handleConfirm() {
      if (this.selectedProxy) {
        this.selectedProxyName = this.selectedProxy.name
        this.$emit('input', this.selectedProxy.id)
        this.$emit('change', this.selectedProxy)
        this.dialogVisible = false
      }
    },
    handleDialogClose() {
      this.dialogVisible = false
    },
    handleClear() {
      this.selectedProxyName = ''
      this.selectedProxy = null
      this.$emit('input', null)
      this.$emit('change', null)
    },
    openDialog() {
      if (this.disabled) return
      this.dialogVisible = true
      this.queryList()
    },
    loadProxyName(proxyId) {
      httpClient.get(`/api/proxy/${proxyId}`).then((response) => {
        if (response.data.data) {
          this.selectedProxyName = response.data.data.name
        }
      }).catch(() => {
        this.selectedProxyName = ''
      })
    },
    sortByName(a, b) {
      return a.name.localeCompare(b.name, 'zh-CN')
    },
    calcTableHeight() {
      // 按视口高度计算固定表格高度，避免数据行数变化导致弹框高度跳动
      this.tableHeight = Math.max(300, Math.floor(window.innerHeight * 0.5))
    }
  },
  mounted() {
    this.calcTableHeight()
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableHeight)
  }
};
</script>

<style scoped>
.proxy-selector {
  display: inline-block;
  width: 100%;
}

.select-button {
  border-left: none;
  border-radius: 0;
}

.search-area {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}
</style>

<style>
.proxy-dialog {
  display: flex;
  flex-direction: column;
  margin: 0 !important;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-height: calc(100% - 30px);
  max-width: calc(100% - 30px);
}

.proxy-dialog .el-dialog__body {
  flex: 1;
  overflow: auto;
}
</style>
